import { ApiError } from '../api/public'

/**
 * The dateline every live view carries, and the banner it grows when something
 * stops arriving.
 *
 * # Nothing on a live view is undated (FR-035e)
 *
 * A scoreboard with no timestamp cannot be told apart from a stale one, and
 * "how old is this?" is exactly the question of somebody standing at the
 * simulator wondering whether their last hole has landed.
 *
 * # Two different silences, said differently
 *
 * They have different causes and different remedies, so they are never
 * collapsed into one "offline" message:
 *
 *   - **This page cannot reach the API.** The record is fine; the phone or the
 *     server is not. Nothing on screen is wrong, it is simply not moving.
 *   - **The API is reachable and no hole has arrived recently** — the server's
 *     own `stale`. The producer has probably died, and the fix is to type the
 *     scores in (FR-018b), which is a different thing to go and do.
 *
 * In both cases **the last known good state stays on screen** (FR-035f,
 * SC-002a). Constitution IV: the night carries on without us.
 */
export function LiveStatus({
  loadedAt,
  sourceUpdatedAt,
  sourceStale,
  failures,
  error,
}: {
  loadedAt: Date | null
  /** The server's own "when did this last change", where the route has one. */
  sourceUpdatedAt?: string
  sourceStale?: boolean
  failures: number
  error: Error | null
}) {
  const offline = failures > 0
  return (
    <div className="live-status">
      <p className="live-updated" data-testid="live-updated">
        <span
          className={`live-dot ${offline ? 'live-dot-off' : 'live-dot-on'}`}
          aria-hidden="true"
        />
        {sourceUpdatedAt ? (
          <>
            Last hole recorded <time dateTime={sourceUpdatedAt}>{clock(sourceUpdatedAt)}</time>
          </>
        ) : (
          <>Live</>
        )}
        {loadedAt && <> · checked {clock(loadedAt.toISOString())}</>}
        {/* Updating with no interaction is the promise (FR-035b, SC-014), so it
            is stated: a reader who sees nothing change should know whether
            that is the page or the golf. */}
        {!offline && <> · updates on its own</>}
      </p>

      {offline && (
        <p className="live-banner live-banner-offline" role="status" data-testid="live-banner">
          <strong>Not updating.</strong> This page cannot reach the league right now, so what is
          below is the last it saw — it is real, just not current. It will pick up again on its own.
          {error && <span className="live-banner-detail"> ({detail(error)})</span>}
        </p>
      )}

      {!offline && sourceStale && (
        <p className="live-banner live-banner-stale" role="status" data-testid="stale-banner">
          <strong>No new holes for a while.</strong> The league is reachable and nothing has arrived
          recently — if play is still going, the scores can be typed in and they will show here
          exactly the same way.
        </p>
      )}
    </div>
  )
}

/** The time of day, which is the only part of a timestamp anyone reads here. */
function clock(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

/** The request id if there is one: what a bug report needs, in one line. */
function detail(error: Error): string {
  const id = error instanceof ApiError ? error.problem.request_id : undefined
  return id ? `${error.message} · request ${id}` : error.message
}

/**
 * The waiting state, which is NOT an error.
 *
 * A live view opened before the first response has nothing to show and says so
 * plainly. It is the only moment a live view is ever empty; after that, there
 * is always a last known good state.
 */
export function LiveLoading({ what }: { what: string }) {
  return (
    <p className="state-loading" role="status">
      Reading {what}…
    </p>
  )
}
